import React, { useMemo, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { Box, TextField, Button, Stack, Typography, CircularProgress, Backdrop } from '@mui/material';
import { useAppParams } from './hooks/useAppParams';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import AppBar from './components/AppBar';
import ProductGrid from './components/ProductGrid';
import CartDrawer from './components/CartDrawer';
import CodeVerificationDialog from './components/CodeVerificationDialog';
import CustomerRegistrationDialog from './components/CustomerRegistrationDialog';
import ErrorBoundary from './components/ErrorBoundary';

import CatalogDrawer from './components/CatalogDrawer';
import { useOrderProducts } from './hooks/useOrderProducts';
import { useWebSettings } from './hooks/useWebSettings';
import { useCatalog } from './hooks/useCatalog';

import { hexToRgba, normalizeHex, getContrastTextColor } from './utils/color';
import { submitOrder } from './api/orderApi';

const PAGE_SIZE = 10;

export default function App() {
    const { t } = useTranslation();


   const { customerId, branchId, businessId } = useAppParams();


    const hasCustomerContext = Boolean(customerId && branchId);
    const needsRegistration  = !hasCustomerContext && Boolean(businessId);

    // UI state
    const [cartItems, setCartItems] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    // Tasdiqlash dialogi
    const [showCodeDialog, setShowCodeDialog] = useState(false);
    const [pendingOrderInfo, setPendingOrderInfo] = useState(null);

    // Web settings
    const { webSettings, loading: wsLoading } = useWebSettings({ businessId, branchId });

    const showPrice = webSettings?.showPriceProduct !== false;

    const showCount = webSettings?.offCount === true;
    const quantityField = hasCustomerContext ? 'remain' : 'remainBusiness';

    // Ranglar
    const appbarHex = webSettings?.buttonColors?.appbarColor || '#111827';
    const appbarNorm = normalizeHex(appbarHex) || '#FF111827';
    const appbarRgba = hexToRgba(appbarNorm);
    const appbarCss  = appbarRgba?.css || 'rgba(17, 24, 39, 1)';
    const appbarText = getContrastTextColor(appbarNorm);

    const btnHex   = webSettings?.buttonColors?.buttonColor || '#0A84FF';
    const btnNorm  = normalizeHex(btnHex) || '#FF0A84FF';
    const btnRgba  = hexToRgba(btnNorm);
    const btnCss   = btnRgba?.css || 'rgba(10,132,255,1)';
    const btnText  = getContrastTextColor(btnNorm);

    const priceHex  = webSettings?.buttonColors?.priceColor || '#10B981';
    const priceNorm = normalizeHex(priceHex) || '#FF10B981';
    const priceRgba = hexToRgba(priceNorm);
    const priceCss  = priceRgba?.css || 'rgba(16,185,129,1)';

    // ====== Katalog (chap menyu) ======
// T O ' G ' R I
    const { catalog, loading: catalogLoading, error: catalogError } = useCatalog({
        businessId,
        branchId,
        webSettings, // web-settings ichidan businessId derivatsiya qilish uchun
    });
    const [catalogOpen, setCatalogOpen] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);

    const openCatalog = () => setCatalogOpen(true);
    const closeCatalog = () => setCatalogOpen(false);

    const handleSelectCategory = (catId) => {
        setSelectedCategoryId(catId);
        setSelectedSubCategoryId(null);
        setPage(0);
    };

    const handleSelectSubCategory = (catId, subId) => {
        setSelectedCategoryId(catId);
        setSelectedSubCategoryId(subId);
        setPage(0);
    };
    // ===================================

    // Mahsulotlar (hook markaziy API orqali + kategoriya filtr)
    const { products, customerName, hasMore } = useOrderProducts({
        enabled: hasCustomerContext,
        customerId,
        branchId,
        search,
        page,
        pageSize: PAGE_SIZE,
        webCategoryId: selectedCategoryId || undefined,
        webSubCategoryId: selectedSubCategoryId || undefined,
    });

    // Header balandligini o'lchash
    const headerRef = useRef(null);
    const [headerH, setHeaderH] = useState(0);
    useLayoutEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        const measure = () => setHeaderH(el.offsetHeight || 0);
        measure();

        let ro;
        if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
            ro = new ResizeObserver(() => measure());
            ro.observe(el);
        } else {
            console.warn('ResizeObserver not available, using resize fallback');
        }
        window.addEventListener('resize', measure);
        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);

    // Sahifa o'zgarganda top'ga chiqish (mavjud xulqni saqlaymiz)
    useEffect(() => {
        if (hasCustomerContext) window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [page, hasCustomerContext]);

    const handleAddToCart = (product, quantity) => {
        setCartItems((prev) => {
            const idx = prev.findIndex((i) => i.id === product.id);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity };
                return copy;
            }
            return [...prev, { ...product, quantity }];
        });
    };

    const handleIncrement = (id) => {
        setCartItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, quantity: item.quantity + 1 } : item))
        );
    };

    const handleDecrement = (id) => {
        setCartItems((prev) =>
            prev
                .map((item) =>
                    item.id === id ? { ...item, quantity: Math.max(0, item.quantity - 1) } : item
                )
                .filter((item) => item.quantity > 0)
        );
    };

    const orderPayload = useMemo(
        () => ({
            customerId,
            branchId,
            items: cartItems.map((item) => ({
                productId: item.type === 'PRODUCT' ? item.id : null,
                productTypePriceId: item.type === 'TYPE' ? item.id : null,
                quantity: item.quantity,
                price: item.price,
            })),
        }),
        [customerId, branchId, cartItems]
    );

    const handleSubmitOrder = async (info) => {
        if (!hasCustomerContext) { alert(t('customerOrBranchNotFound')); return; }
        if (orderPayload.items.length === 0) { alert(t('cartIsEmpty') || 'Savatcha bo‘sh'); return; }

        const dto = {
            ...orderPayload,
            note: info?.note || '',
            deliveryTime: info?.deliveryTime || '',
            paymentMethod: info?.paymentMethod || (
                (webSettings?.showOfflinePay !== false) ? 'OFFLINE' :
                    (webSettings?.showClickPay ? 'CLICK' : 'OFFLINE')
            ),
        };

        try {
            setSubmitting(true);

            const resp = await submitOrder(dto);

            // 🛡️ Guard — agar yana undefined bo‘lsa, diagnostika ko‘rsatamiz
            if (!resp) {
                console.error('submitOrder returned falsy value:', resp);
                alert('Server javobi bo‘sh keldi (resp undefined).');
                return;
            }

            console.log('Submit resp (normalized):', resp);

            // CLICK oqimi: paymentLink ni 3 joydan qidiramiz
            const paymentLink =
                resp?.data?.paymentLink ||
                resp?.object?.paymentLink ||
                resp?.paymentLink;

            if (resp?.success && paymentLink) {
                // aynan bu handler ichida redirect qiling (popup block bo‘lmasligi uchun)
                window.location.assign(paymentLink);
                return;
            }

            // Fallback: CLICK_REDIRECT bo‘lsa, lekin link topilmasa
            if (resp?.success && (resp?.message === 'CLICK_REDIRECT' || resp?.message === 'CLICK')) {
                console.error('CLICK_REDIRECT keldi, ammo paymentLink yo‘q. Full resp:', resp);
                alert('CLICK paymentLink topilmadi. Iltimos, qayta urinib ko‘ring.');
                return;
            }

            // OFFLINE / qolgan holatlar
            if (resp?.success) {
                alert(t('orderSuccess') || 'Buyurtma yuborildi');
                setCartItems([]);
                setCartOpen(false);
            } else if ((resp?.message || '').toLowerCase().includes('tasdiqlash kodini kiriting')) {
                setPendingOrderInfo(info);
                setShowCodeDialog(true);
            } else {
                alert(t('orderError', { message: resp?.message }) || (resp?.message || 'Xatolik'));
            }
        } catch (err) {
            console.error('Submit error:', err);
            alert(t('orderSubmitError', { message: err?.message }) || err?.message || 'Xatolik');
        } finally {
            setSubmitting(false);
        }
    };



    const handleCodeVerified = () => {
        setShowCodeDialog(false);
        if (pendingOrderInfo) {
            const info = pendingOrderInfo;
            setPendingOrderInfo(null);
            handleSubmitOrder(info);
        }
    };

    return (
        <ErrorBoundary>
            <Helmet>
                <title>aiDEX</title>
                <meta name="theme-color" content={appbarCss} />
            </Helmet>

            {/* Chapdagi Katalog Drawer */}
            <CatalogDrawer
                open={catalogOpen}
                onClose={closeCatalog}
                catalog={catalog}
                loading={catalogLoading}      // <-- qo'shildi
                error={catalogError}          // <-- qo'shildi
                onSelectCategory={handleSelectCategory}
                onSelectSubCategory={handleSelectSubCategory}
                selectedCategoryId={selectedCategoryId}
                selectedSubCategoryId={selectedSubCategoryId}
            />


            <Box sx={{ width: '100%', minHeight: '100vh', overflowX: 'hidden', touchAction: 'pan-y' }}>
                {hasCustomerContext ? (
                    <>
                        <Box
                            ref={headerRef}
                            sx={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                zIndex: (t) => t.zIndex.appBar,
                                bgcolor: 'transparent',
                                boxShadow: (t) => t.shadows[2],
                                pt: 'env(safe-area-inset-top)',
                            }}
                        >
                            <AppBar
                                customerName={customerName}
                                cartCount={cartItems.length}
                                onCartClick={() => setCartOpen(true)}
                                onOpenCatalog={openCatalog}
                                bgColor={appbarCss}
                                fgColor={appbarText}
                                loading={wsLoading || catalogLoading}
                            />
                            <Box sx={{ px: { xs: 1, sm: 1.5, md: 2 }, py: 1 }}>
                                <TextField
                                    fullWidth
                                    label={t('searchPlaceholder') || 'Qidirish'}
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setPage(0);
                                    }}
                                    sx={{ bgcolor: 'background.paper' }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pb: 2, pt: `${headerH}px` }}>
                            <ProductGrid
                                products={products}
                                onAddToCart={handleAddToCart}
                                buttonColor={btnCss}
                                buttonTextColor={btnText}
                                priceColor={priceCss}
                                showPrice={showPrice}
                                // ⬇️ QO‘SHILDI
                                showCount={showCount}
                                quantityField={quantityField}
                            />

                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                                <Button variant="contained" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                                    {t('previous') || 'Oldingi'}
                                </Button>
                                <Button variant="contained" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
                                    {t('next') || 'Keyingi'}
                                </Button>
                            </Stack>
                        </Box>

                        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999 }} open={submitting}>
                            <Box textAlign="center">
                                <CircularProgress color="inherit" />
                                <Typography mt={2}>{t('submittingOrder') || 'Buyurtma yuborilmoqda...'}</Typography>
                            </Box>
                        </Backdrop>

                        <CartDrawer
                            open={cartOpen}
                            onClose={() => setCartOpen(false)}
                            cartItems={cartItems}
                            onSubmit={handleSubmitOrder}
                            onIncrement={handleIncrement}
                            onDecrement={handleDecrement}
                            buttonColor={btnCss}
                            buttonTextColor={btnText}
                            priceColor={priceCss}
                            showPrice={showPrice}
                            // ⬇️ MUHIM: shu ikkisini CartDrawer’ga bering
                            showClickPay={webSettings?.showClickPay === true}
                            showOfflinePay={webSettings?.showOfflinePay !== false}
                        />


                        <CodeVerificationDialog
                            open={showCodeDialog}
                            onClose={() => setShowCodeDialog(false)}
                            customerId={customerId}
                            onVerified={handleCodeVerified}
                            buttonColor={btnCss}
                            buttonTextColor={btnText}
                        />
                    </>
                ) : needsRegistration ? (
                    <CustomerRegistrationDialog open onClose={() => {}} businessId={businessId} onRegistered={() => {}} />
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h6">{t('paramsMissingTitle') || 'Parametrlar topilmadi'}</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {t('paramsMissingBody') || 'Iltimos, bot orqali sahifaga o‘ting yoki tegishli linkdan foydalaning.'}
                        </Typography>
                    </Box>
                )}
            </Box>
        </ErrorBoundary>
    );
}
